import { Box, Button, CircularProgress } from '@mui/material';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { memo, useCallback, useState, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import strings from '../Globalization/index';
import { useGridBase } from '../components/grid/GridBaseContext';
import consts from '../consts';
import Model from '../models/model';
import Connection from '../services/Connection';
import util from '../util';
import { dataStore } from '../Store';
import { usePrerequisites } from '../contexts/Prerequisites';

const { showError, viewOnly, checkUserRoleAccess } = util;
const { HuluInterfaceConfig } = Connection;
const { InterfaceType, DataSource } = consts;
const clearDataRoles = ['steris admin', 'Steris Specialist']

const CustomButton = styled(Button)(() => ({
	backgroundColor: '#F8996B',
	color: 'white',
	'&:hover': {
		backgroundColor: '#F8996B',
	},
}));


const sendTopic = async ({ row }) => {
	const formValue = new FormData();
	formValue.append('interfaceName', row.InterfaceName);
	formValue.append('dataSource', row.DataSource)
	formValue.append('otherAction', 'publicTopic');
	formValue.append('action', 'otherAction');
	const response = await fetch(`${HuluInterfaceConfig}`, {
		method: 'POST',
		body: formValue
	});
	return await response.json();
};
const ResendFileBtn = ({ row }) => {
	const { patch, isLoading, refresh } = useGridBase();
	const handleClick = async () => {
		patch({ isLoading: true });
		if(!row.Active) {
			showError(strings.InterfaceInactive, strings.reSendFile, 'info');
			patch({ isLoading: false });
			return;
		}
		const response = await sendTopic({ row });
		if (response.success) {
			patch({ isLoading: false });
			showError(strings[response.message], strings.reSendFile, 'info');
		}
		refresh();
	};
	return (
		<Button
			onClick={handleClick}
			color="primary"
			variant="contained"
			title={strings.reSend}
			size="small"
			disabled={row.InterfaceName?.toLowerCase() === 'hulusync' || isLoading}
		>
			{strings.reSend}
		</Button>
	);
};
const SelectBtn = ({ row = {} }) => {
	const { InterfaceName = '' } = row;

	return (
		<Button
			component={Link}
			to={`/endoProfile?interFaceType=${InterfaceName}&returnTo=${encodeURIComponent('/interfaceConfig')}`}
			variant="contained"
		 	color="primary"
			size="small"
			disabled={!checkUserRoleAccess(['steris specialist', 'steris admin']) || row.InterfaceName?.toLowerCase() === 'hulusync'}
		>
			{strings.ClearAll}
		</Button>
	);
};
const ClearScopeUserBtn = memo(() => {
	const { filterModel } = useGridBase();
	const [loading, setLoading] = useState(false);
	const { auth = {} } = dataStore?.getState() || {};
	const { tags = {} } = auth;
	const [, { getLicenseType }] = usePrerequisites();
	const licenceType = typeof getLicenseType === 'function' ? getLicenseType() : {};
	const hasCATExportAndEndo =
		licenceType?.products &&
		licenceType?.products.includes('catendo');
	const handleOnClick = useCallback(() => {
		Swal.fire({
			title: strings.WarningTitle,
			text: strings.ScopeUserDataConfirmation,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: strings.Ok,
			cancelButtonText: strings.Cancel,
			confirmButtonColor: '#3085d6'
		}).then((result) => {
			if (result.isConfirmed) {
				setLoading(true);
				const formData = new FormData();
				formData.append('otherAction', 'clearScopeUser');
				formData.append('action', 'otherAction');
				fetch(Connection.AppConfig, {
					method: 'POST',
					body: formData
				})
					.then(async (res) => {
						let json = {};
						try {
							json = await res.json();
						} catch (error) {
							console.error(error);
						}
						return json;
					})
					.then(({ success, info }) => {
						setLoading(false);
						if (success) {
							showError(strings.ScopeUserDataUpdated, strings.Success, 'success');
						} else {
							showError(strings.SomethingWentWrongScopeUserData, strings.ErrorTitle, 'error');
						}
					})
					.catch((error) => {
						setLoading(false);
						showError(strings.SomethingWentWrongScopeUserData, strings.ErrorTitle, 'error');
					});
			}
		});
	}, [filterModel]);
	const isVisible = useMemo(() => clearDataRoles.includes(tags?.RoleName?.toLowerCase()) && hasCATExportAndEndo, []);
	if (isVisible) {
		return (<Box display="flex">
			<CustomButton
				onClick={handleOnClick}
				variant="contained"
				size="small"
				disabled={loading}
			>
				{strings.ClearScopeUserData}
			</CustomButton>
			{loading && <CircularProgress size={16} />}
		</Box>)
	}
});

export const model = new Model({
	name: 'HuluInterfaceConfig',
	idProperty: 'id',
	pageSize: 10,
	disableDelete: true,
	disableAdd: true,
	disableExport: true,
	disableFiltering: true,
	hideGridFooter: true,
	title: 'InterfaceConfiguration',
	formTitleField: 'InterfaceName',
	showGridRefreshBtn: true,
	gridToolbarCustomActions: [
		{
			Component: ClearScopeUserBtn
		}
	],
	customizeSaveData: (toSave, { activeRecord = {} }) => {
		const { HULURegion = [] } = activeRecord.combos || [];
		const { RegionName } = activeRecord.record || {};
		toSave.UnmodifiedRegion = RegionName;
		toSave.RegionName = HULURegion.find(region => region.LookupId === toSave.RegionId).DisplayValue || RegionName;
		return toSave
	},
	columns: [
		{ field: 'InterfaceName', header: 'interfaceName', readOnly: true, width: 150 },
		{ field: 'InterfaceDesc', header: 'interfaceDesc', readOnly: true, width: 150 },
		{ field: 'InterfaceType', header: 'InterfaceType', readOnly: true, width: 150 },
		{
			field: 'DataSource',
			width: 150,
			lookup: "DataSource",
			onChange: ({ value, formik, activeRecord }) => {
				if (activeRecord.record.DataSource === DataSource.CAT && value === DataSource.TrackingSystem) {
					Swal.fire({ icon: 'warning', title: strings.Disclaimer, text: strings.DataSourceDisclaimer, confirmButtonText: strings.Ok, allowOutsideClick: false }).then(({ isConfirmed }) => {
						if (isConfirmed) {
							formik.setFieldValue('DataSource', value)
						}
					}).catch((err) => {
						console.error(err)
					});
				} else {
					formik.setFieldValue('DataSource', value)
				}
			},
			readOnly: ({ formik }) => {
				if (formik.values.DataSource === DataSource.HULU) {
					return true
				}
				return false;
			}
		},
		{
			field: 'CountryCode',
			header: 'CountryCode',
			readOnly: true,
			hidden: true,
			width: 150
		},
		{
			formField: 'RegionId',
			field: 'RegionName',
			lookup: 'HULURegion',
			header: 'HuluRegion',
			width: 150,
			type: 'number',
			hidden: true,
			readOnly: ({ formik = {} }) => {
				return viewOnly(false, formik, ['steris specialist'], true)
			},
			required: true,
			inputWhen: ({ activeRecord }) => {
				return activeRecord?.record?.InterfaceName === 'ScopeFile'
			}
		},
		{

			field: 'Active',
			width: 150,
			type: 'bool',
			onChange: ({ value, formik }) => {
				Swal.fire({
					icon: 'warning', title: strings.Disclaimer, text: strings.ActiveDisclaimer,
					showCancelButton: true,
					confirmButtonText: strings.Yes,
					cancelButtonText: strings.No,
					allowOutsideClick: false
				}).then(({ isDismissed, isConfirmed }) => {
					if (isDismissed) {
						formik.setFieldValue("Active", formik.values.Active)
					}
					if (isConfirmed) {
						formik.setFieldValue("Active", value)
					}

				}).catch((err) => {
					console.error(err)
				});
			}
		},
		{
			field: 'LastRunDateTime',
			header: 'LastRunDateTime',
			type: "dateTime",
			isLocal: true,
			readOnly: ({ formik }) => {
				if (formik.values.DataSource === DataSource.HULU && formik.values.InterfaceType === InterfaceType.API) {
					return false
				}
				return true;
			},
			width: 170
		},
		{
			field: 'Action',
			header: 'reSendFile',
			width: 170,
			filterable: false,
			isNotformfield: true,
			renderCell: ResendFileBtn
		},
		{
			field: 'ClearAllResync',
			header: 'ClearAllResync',
			width: 170,
			filterable: false,
			isNotformfield: true,
			renderCell: SelectBtn
		}
		
	]
});

export default model.createFormBase();
